import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, App, Avatar, Button, Card, Checkbox, Col, Form, InputNumber, Row, Select, Space, Switch, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';
import { WEEKDAYS, type SequenceItem, type SequenceSettings } from '@crm/shared';
import { useAgents } from '../../features/email/api';
import { useSettings } from '../../features/settings/api';
import { useUpdateSequence } from '../../features/sequences/api';
import { CardTitle } from '../../components/StatCard';
import { errorMessage } from '../../api/client';
import { ActionsEditor, actionsComplete } from './ActionsEditor';

/** Every IANA zone the browser knows (falls back to a few common ones). */
const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

function Toggle({ checked, onChange, label, help }: { checked: boolean; onChange: (v: boolean) => void; label: string; help?: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <Switch aria-label={label} checked={checked} onChange={onChange} style={{ marginTop: 2 }} />
      <div>
        <Typography.Text>{label}</Typography.Text>
        {help && <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>{help}</Typography.Text></div>}
      </div>
    </div>
  );
}

export function SettingsTab({ sequence }: { sequence: SequenceItem }) {
  const { message } = App.useApp();
  const agents = useAgents();
  const workspace = useSettings();
  const update = useUpdateSequence();
  const savedAgents = useMemo(() => sequence.agents.map((a) => a.id), [sequence.agents]);
  const [s, setS] = useState<SequenceSettings>(sequence.settings);
  const [agentIds, setAgentIds] = useState<string[]>(savedAgents);
  const dirty = JSON.stringify(s) !== JSON.stringify(sequence.settings) || JSON.stringify(agentIds) !== JSON.stringify(savedAgents);

  useEffect(() => {
    if (!dirty) {
      setS(sequence.settings);
      setAgentIds(savedAgents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequence.settings, savedAgents]);

  const set = (patch: Partial<SequenceSettings>) => setS((x) => ({ ...x, ...patch }));
  const save = () => {
    if (!actionsComplete(s.onReply) || !actionsComplete(s.onFinish)) return message.warning('Finish the reply and finish rules (pick a tag, list, value or task title)');
    if (s.minGapMinutes > s.maxGapMinutes) return message.warning('The shortest pause must not be longer than the longest');
    update
      .mutateAsync({ id: sequence.id, settings: s, agentIds })
      .then(() => message.success('Settings saved'))
      .catch((e) => message.error(errorMessage(e)));
  };

  const active = (agents.data ?? []).filter((a) => a.isActive || agentIds.includes(a.id));
  const perDayCapacity = agentIds.length * s.perAgentPerDay;
  const zone = s.timezone ?? workspace.data?.timezone ?? 'UTC';

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {dirty && (
        <Alert
          type="warning"
          title="You have unsaved settings."
          action={
            <Space>
              <Button size="small" onClick={() => { setS(sequence.settings); setAgentIds(savedAgents); }}>Discard</Button>
              <Button size="small" type="primary" loading={update.isPending} onClick={save}>Save settings</Button>
            </Space>
          }
        />
      )}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title={<CardTitle title="Senders" subtitle="Each person is emailed by one agent for the whole sequence, so follow-ups stay in one thread. People are spread evenly over the agents." />}>
            <Select
              mode="multiple"
              aria-label="Agents"
              style={{ width: '100%' }}
              placeholder={agents.isLoading ? 'Loading agents…' : 'Pick the agents who send'}
              loading={agents.isLoading}
              value={agentIds}
              onChange={setAgentIds}
              optionFilterProp="label"
              notFoundContent="No agents. Create one under Settings → Agents."
              options={active.map((a) => ({ value: a.id, label: `${a.name} <${a.email}>`, disabled: !a.isActive, avatarUrl: a.avatarUrl, name: a.name }))}
              optionRender={(o) => (
                <Space>
                  <Avatar size="small" src={(o.data as { avatarUrl: string | null }).avatarUrl ?? undefined} style={{ background: '#722ed1' }}>{String((o.data as { name: string }).name)[0]}</Avatar>
                  {o.label}
                </Space>
              )}
            />
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
              Replies go to the agent's address and show up in the Inbox with the whole conversation. Make sure the agent's domain receives mail in the CRM (Settings → Email domains).
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title={<CardTitle title="Sending window" subtitle="Emails only go out on these days and hours." />}>
            <Form layout="vertical" component="div">
              <Form.Item label="Days" style={{ marginBottom: 12 }}>
                <Checkbox.Group value={s.sendDays} onChange={(v) => set({ sendDays: (v as number[]).sort() })} options={[1, 2, 3, 4, 5, 6, 0].map((d) => ({ value: d, label: WEEKDAYS[d] }))} />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Hours" style={{ marginBottom: 12 }}>
                    <TimePicker.RangePicker
                      aria-label="Sending hours"
                      format="HH:mm"
                      minuteStep={15}
                      allowClear={false}
                      value={[dayjs(s.sendFrom, 'HH:mm'), dayjs(s.sendTo, 'HH:mm')]}
                      onChange={(v) => v?.[0] && v[1] && set({ sendFrom: v[0].format('HH:mm'), sendTo: v[1].format('HH:mm') })}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Time zone" style={{ marginBottom: 12 }}>
                    <Select
                      aria-label="Time zone"
                      showSearch
                      allowClear
                      placeholder={`Workspace (${workspace.data?.timezone ?? 'UTC'})`}
                      value={s.timezone ?? undefined}
                      onChange={(v) => set({ timezone: v ?? null })}
                      options={ZONES.map((z) => ({ value: z, label: z }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {s.sendDays.length ? `${s.sendDays.map((d) => WEEKDAYS[d]).join(', ')}, ${s.sendFrom}–${s.sendTo} ${zone}` : 'No days picked: nothing will be sent.'}
              </Typography.Text>
            </Form>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title={<CardTitle title="Daily limits" subtitle="Slow and steady keeps mailboxes out of spam. Domain warm-up limits apply on top." />}>
            <Form layout="vertical" component="div">
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="New people per day" extra="First emails a day">
                    <InputNumber aria-label="New people per day" min={1} max={5000} value={s.newPerDay} onChange={(v) => set({ newPerDay: v ?? 1 })} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Emails per day" extra="First emails and follow-ups">
                    <InputNumber aria-label="Emails per day" min={1} max={20000} value={s.maxPerDay} onChange={(v) => set({ maxPerDay: v ?? 1 })} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Per agent per day" extra="Over all sequences">
                    <InputNumber aria-label="Emails per agent per day" min={1} max={5000} value={s.perAgentPerDay} onChange={(v) => set({ perAgentPerDay: v ?? 1 })} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Pause between emails" extra="Random, per agent (minutes)">
                    <Space.Compact>
                      <InputNumber aria-label="Shortest pause" min={1} max={240} value={s.minGapMinutes} onChange={(v) => set({ minGapMinutes: v ?? 1 })} style={{ width: 80 }} />
                      <InputNumber aria-label="Longest pause" min={1} max={240} value={s.maxGapMinutes} onChange={(v) => set({ maxGapMinutes: v ?? 1 })} style={{ width: 80 }} prefix="to" />
                    </Space.Compact>
                  </Form.Item>
                </Col>
              </Row>
              {agentIds.length > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Up to {Math.min(s.maxPerDay, perDayCapacity)} emails a day with {agentIds.length} agent{agentIds.length === 1 ? '' : 's'}.
                </Typography.Text>
              )}
            </Form>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title={<CardTitle title="Tracking & deliverability" />}>
            <Space orientation="vertical" size={14}>
              <Toggle checked={s.trackOpens} onChange={(v) => set({ trackOpens: v })} label="Track opens" help="Adds an invisible 1×1 image. Apple Mail and some security filters open emails on their own, so treat opens as a rough signal." />
              <Toggle checked={s.trackClicks} onChange={(v) => set({ trackClicks: v })} label="Track link clicks" help="Links go through a redirect on your CRM's domain." />
              <Toggle checked={s.listUnsubscribeHeader} onChange={(v) => set({ listUnsubscribeHeader: v })} label="One-click unsubscribe header" help="Gmail and Yahoo show an “Unsubscribe” button. Required for bulk senders, but it makes an email look less personal." />
              <Toggle checked={s.requireVerified} onChange={(v) => set({ requireVerified: v })} label="Only email verified addresses" help="Off: the email statuses picked on Tools → Verification are emailed." />
              {s.requireVerified && (
                <div style={{ paddingLeft: 40 }}>
                  <Toggle checked={s.includeUnconfirmed} onChange={(v) => set({ includeUnconfirmed: v })} label="Also Unknown and Catch-all" help="A delivery marks them Valid, a hard bounce Invalid." />
                </div>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24}>
          <Card title={<CardTitle title="Rules" subtitle="A reply always ends the sequence for that person. The thread then shows in the Inbox with every email sent so far." />}>
            <Row gutter={[24, 16]}>
              <Col xs={24} lg={12}>
                <Space orientation="vertical" size={14}>
                  <Toggle checked={s.ignoreAutoReplies} onChange={(v) => set({ ignoreAutoReplies: v })} label="Ignore out-of-office and automatic replies" help="They still land in the Inbox but don't stop the sequence." />
                  <Toggle checked={s.stopOnOrgReply} onChange={(v) => set({ stopOnOrgReply: v })} label="Stop for the whole organization on a reply" help="When someone replies, colleagues at the same organization get no more emails." />
                  <Toggle checked={s.stopOnClick} onChange={(v) => set({ stopOnClick: v })} label="Finish when a link is clicked" />
                  <Toggle checked={s.skipActiveElsewhere} onChange={(v) => set({ skipActiveElsewhere: v })} label="Skip people active in another sequence" />
                  <Toggle checked={s.autoEnroll} onChange={(v) => set({ autoEnroll: v })} label="Add new people automatically" help="Anyone who starts matching the audience joins (checked every 15 minutes)." />
                  <Toggle checked={s.showInInboxBeforeReply} onChange={(v) => set({ showInInboxBeforeReply: v })} label="Show threads in the Inbox before a reply" help="Off keeps the Inbox for conversations: threads appear once the person replies." />
                </Space>
              </Col>
              <Col xs={24} lg={12}>
                <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                  <div>
                    <Typography.Text strong>When someone replies</Typography.Text>
                    <div style={{ marginTop: 8 }}><ActionsEditor value={s.onReply} onChange={(onReply) => set({ onReply })} addLabel="Add reply action" /></div>
                  </div>
                  <div>
                    <Typography.Text strong>When someone finishes without replying</Typography.Text>
                    <div style={{ marginTop: 8 }}><ActionsEditor value={s.onFinish} onChange={(onFinish) => set({ onFinish })} addLabel="Add finish action" /></div>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <div style={{ textAlign: 'right' }}>
        <Button type="primary" disabled={!dirty} loading={update.isPending} onClick={save}>Save settings</Button>
      </div>
    </Space>
  );
}
